export interface ForumAuthor {
  displayName: string;
  anonAlias: string | null;
  isStaff: boolean;
  isOwnPost: boolean;
  authorRealName?: string;
  authorEmail?: string;
}

export interface ForumPost {
  id: string;
  body: string;
  createdAt: string;
  author: ForumAuthor;
}

export interface ForumThreadSummary {
  id: string;
  title: string;
  excerpt: string;
  isPinned: boolean;
  isLocked: boolean;
  createdAt: string;
  updatedAt: string;
  replyCount: number;
  author: ForumAuthor;
}

export interface ForumThreadDetail extends ForumThreadSummary {
  posts: ForumPost[];
}

export interface ForumOverview {
  enabled: boolean;
  myAnonAlias?: string | null;
  threads: ForumThreadSummary[];
}

export function formatForumDate(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function formatForumLastReply(updatedAt: string, replyCount: number): string {
  if (replyCount === 0) return 'Sin respuestas aún';

  const diffSec = Math.round((new Date(updatedAt).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
  ];

  for (const [unit, seconds] of units) {
    if (Math.abs(diffSec) >= seconds) {
      return `Última respuesta ${rtf.format(Math.round(diffSec / seconds), unit)}`;
    }
  }

  return `Última respuesta ${rtf.format(0, 'second')}`;
}

export function forumThreadAvatarInitials(thread: ForumThreadSummary): string {
  const suffix = thread.author.anonAlias?.match(/_([A-Za-z0-9]+)$/)?.[1];
  if (suffix) return suffix.slice(0, 2).toUpperCase();
  if (thread.author.isStaff) return 'DC';
  if (thread.author.isOwnPost) return 'TÚ';
  return thread.author.displayName.slice(0, 2).toUpperCase();
}

export function compactPersonName(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  const unique: string[] = [];
  for (const part of parts) {
    if (!unique.some((item) => item.toLowerCase() === part.toLowerCase())) unique.push(part);
  }
  return unique.join(' ');
}

export function forumVisibleAuthor(
  author: ForumAuthor,
  staffView: boolean,
): { name: string; alias: string | null } {
  if (staffView) {
    const name = compactPersonName(author.authorRealName?.trim() || author.displayName);
    const alias = author.anonAlias && author.anonAlias !== name ? author.anonAlias : null;
    return { name, alias };
  }

  if (author.isStaff) return { name: author.displayName, alias: null };
  return { name: author.anonAlias || author.displayName, alias: null };
}

export function forumPostAvatarInitials(post: ForumPost): string {
  const suffix = post.author.anonAlias?.match(/_([A-Za-z0-9]+)$/)?.[1];
  if (suffix) return suffix.slice(0, 2).toUpperCase();
  if (post.author.isStaff) {
    const name = post.author.authorRealName ?? post.author.displayName;
    return name.slice(0, 2).toUpperCase() || 'DC';
  }
  const name = post.author.authorRealName ?? post.author.displayName;
  return name.slice(0, 2).toUpperCase() || '??';
}
